import { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch } from '../fetchInterceptor';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Activity, FileSignature, Sparkles, Shield, ScrollText, Clock,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Visual config per activity kind — controls icon + accent color in the
// timeline. Centralized so adding a new kind in the backend
// (_collect_*_activity) only needs one entry here.
const KIND_CONFIG = {
  audit:             { icon: ScrollText,    label: 'Edits',          dot: 'bg-zinc-400' },
  signature_sent:    { icon: FileSignature, label: 'Signatures',     dot: 'bg-blue-500' },
  ai_buck_feedback:  { icon: Sparkles,      label: 'AI Buck',        dot: 'bg-amber-500' },
  access_request:    { icon: Shield,        label: 'Access',         dot: 'bg-emerald-500' },
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
};

/**
 * MemberActivityTimeline — per-user activity log inside a workspace.
 *
 * Renders a chronological stream merged from audit_events, esign_requests,
 * ai_buck_feedback, and access_requests. Members can view their own;
 * admins/owners can pass any `userId` to view a teammate's.
 *
 * Usage:
 *   <MemberActivityTimeline userId={member.user_id} />        // admin
 *   <MemberActivityTimeline />                                // self
 */
export default function MemberActivityTimeline({ userId: userIdProp }) {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const wsId = currentWorkspace?.workspace_id;
  const userId = userIdProp || user?.user_id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);  // null = all kinds
  const [windowDays, setWindowDays] = useState(30);

  const load = useCallback(async () => {
    if (!wsId || !userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(windowDays) });
      if (filter) params.set('kinds', filter);
      const r = await apiFetch(
        `${API_URL}/api/workspaces/${wsId}/members/${userId}/activity?${params.toString()}`,
      );
      if (r.ok) {
        setData(await r.json());
      } else {
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [wsId, userId, filter, windowDays]);

  useEffect(() => { load(); }, [load]);

  const kinds = useMemo(() => {
    if (!data?.counts) return [];
    return Object.entries(data.counts).sort((a, b) => b[1] - a[1]);
  }, [data]);

  if (!wsId || !userId) return null;

  return (
    <div className="space-y-4" data-testid="member-activity-timeline">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-600" />
            {data?.user?.name || 'Activity timeline'}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {data?.user?.email}
            {data?.user?.role && (
              <Badge variant="secondary" className="ml-2 text-[10px]">
                {data.user.role}
              </Badge>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <Button
              key={d}
              size="sm"
              variant={windowDays === d ? 'default' : 'outline'}
              className="h-7 px-2 text-xs"
              onClick={() => setWindowDays(d)}
              data-testid={`activity-window-${d}d`}
            >
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {/* Filter chips */}
      {kinds.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            size="sm"
            variant={filter === null ? 'default' : 'outline'}
            className="h-6 px-2 text-[11px]"
            onClick={() => setFilter(null)}
            data-testid="activity-filter-all"
          >
            All · {data.total}
          </Button>
          {kinds.map(([kind, count]) => {
            const cfg = KIND_CONFIG[kind] || { label: kind, dot: 'bg-zinc-400' };
            return (
              <Button
                key={kind}
                size="sm"
                variant={filter === kind ? 'default' : 'outline'}
                className="h-6 px-2 text-[11px] flex items-center gap-1.5"
                onClick={() => setFilter(filter === kind ? null : kind)}
                data-testid={`activity-filter-${kind}`}
              >
                <span className={cn('w-1.5 h-1.5 rounded-full', cfg.dot)} />
                {cfg.label} · {count}
              </Button>
            );
          })}
        </div>
      )}

      {/* Timeline */}
      <Card>
        <CardContent className="p-4">
          {loading ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              Loading activity…
            </div>
          ) : !data || data.items.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground" data-testid="activity-empty">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No activity in the last {windowDays} days.
            </div>
          ) : (
            <div className="relative pl-6 space-y-3">
              {/* Vertical track */}
              <div className="absolute left-2 top-2 bottom-2 w-px bg-border" aria-hidden="true" />
              {data.items.map((item) => {
                const cfg = KIND_CONFIG[item.kind] || { icon: Activity, dot: 'bg-zinc-400' };
                const Icon = cfg.icon;
                return (
                  <div
                    key={item.id}
                    className="relative flex items-start gap-3 group"
                    data-testid={`activity-item-${item.kind}`}
                  >
                    <span
                      className={cn(
                        'absolute -left-4 top-1.5 w-2 h-2 rounded-full ring-2 ring-background',
                        cfg.dot,
                      )}
                      aria-hidden="true"
                    />
                    <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">{item.title}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {formatRelative(item.at)}
                        {item.resource_id && (
                          <span className="ml-2 opacity-60">· {item.resource_id}</span>
                        )}
                        {item.status && (
                          <Badge variant="outline" className="ml-2 text-[9px] py-0 px-1.5">
                            {item.status}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
